import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import validator from 'validator';
import FormControl from '@mui/material/FormControl';
import { makeStyles } from '@mui/styles';
import StoreInput from '../../../Components/Forms/Store/StoreInput.js';
import Captcha from '../../../Components/Captcha.js';
import { getEnvVar } from '../../../helpers/env.js';

// e2e money-path: a headless browser can't solve the Cloudflare Turnstile challenge, so under
// the stub flag we skip the real widget and seed a canned token. The api turnstile stub accepts
// any token (verify→true), and registerUser only requires the token to be non-empty. Inert in
// prod builds — the flag is never set there.
const e2eStub = getEnvVar('E2E_STUB_MODE') === 'true';

const useStyles = makeStyles((theme) => ({
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(1),
    '@media all and (max-width: 480px)': {
      flexDirection: 'column',
      width: 'auto',
      '& div:nth-child(1)': {
        display: 'flex!important',
        flexDirection: 'column'
      }
    }
  }
}));

const RegisterForm = ({ onComplete, onError }) => {
  const classes = useStyles();
  const [fields, setFields] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    captchaVerificationKey: e2eStub ? 'e2e-captcha-token' : ''
  });
  const [fieldErrors, setFieldErrors] = useState({});

  const validate = useCallback((newFields, newFieldErrors) => {
    const errMessages = Object.keys(newFieldErrors).filter((k) => newFieldErrors[k]);
    if (!newFields.password) return false;
    if (!newFields.email) return false;
    if (errMessages.length) return false;
    if (!newFields.captchaVerificationKey) return false;
    return true;
  }, []);

  const handleInputChange = ({ id, value, error }) => {
    setFields((state) => {
      const newState = { ...state, [id]: value };
      const isValid = validate(newState, fieldErrors);
      onComplete(isValid, newState);
      return newState;
    });
    setFieldErrors((state) => {
      const newState = { ...state, [id]: error };
      const isValid = validate(fields, newState);
      onComplete(isValid, fields);
      return newState;
    });
  };

  const handleCaptchaSuccess = useCallback((key) => {
    setFields((state) => {
      const newState = { ...state, captchaVerificationKey: key };
      const isValid = validate(newState, fieldErrors);
      onComplete(isValid, newState);
      return newState;
    });
  }, [validate, setFields, onComplete, fieldErrors]);

  const handleCaptchaError = useCallback((error) => {
    setFields((state) => ({ ...state, captchaVerificationKey: '' }));
    if (onError) {
      onError(error);
    }
  }, [onError, setFields]);

  // e2e: production fires its authoritative onComplete from the Captcha success callback — the
  // last event after the fields are typed. With the widget skipped that final call is gone, and
  // handleInputChange's second onComplete runs against a stale `fields` closure, so the last keystroke
  // reports the form incomplete. Re-assert the real validity after each field change under the flag.
  //
  // Guard on the validity RESULT: onComplete's identity changes on every parent (CheckoutWrapper)
  // render and useCheckout's reducer returns a fresh state object even for an unchanged value, so an
  // unconditional call here ping-pongs into an infinite render loop that pegs /checkout. Firing only
  // when validity flips breaks the loop while still reporting completion at the moment it matters.
  const lastReportedValidity = useRef(null);
  useEffect(() => {
    if (!e2eStub) {
      return;
    }
    const valid = validate(fields, fieldErrors);
    if (lastReportedValidity.current === valid) {
      return;
    }
    lastReportedValidity.current = valid;
    onComplete(valid, fields);
  }, [fields, fieldErrors, onComplete, validate]);

  return (
    <form action='' className={classes.form}>
      <FormControl
        variant='standard'
        margin='normal'
        required
        fullWidth
      >
        <StoreInput
          id='firstName'
          label='first-name'
          autoComplete='given-name'
          autoFocus
          required
          maxLength={100}
          value={fields.firstName}
          onChange={handleInputChange}
          validate={(val) => (val ? '' : 'First name is required')}
          dataTestId='register-first-name'
        />
      </FormControl>
      <FormControl
        variant='standard'
        margin='normal'
        required
        fullWidth
      >
        <StoreInput
          id='lastName'
          label='last-name'
          autoComplete='family-name'
          required
          maxLength={100}
          value={fields.lastName}
          onChange={handleInputChange}
          validate={(val) => (val ? '' : 'Last name is required')}
          dataTestId='register-last-name'
        />
      </FormControl>
      <FormControl variant='standard' margin='normal' required fullWidth>
        <StoreInput
          id='email'
          label='email-address'
          autoComplete='email'
          required
          maxLength={100}
          value={fields.email}
          onChange={handleInputChange}
          validate={(val) => (validator.isEmail(val) ? '' : 'Invalid Email')}
          dataTestId='register-email'
        />
      </FormControl>
      <FormControl variant='standard' margin='normal' required fullWidth>
        <StoreInput
          id='password'
          name='password'
          type='password'
          label='password'
          autoComplete='current-password'
          required
          maxLength={100}
          value={fields.password}
          onChange={handleInputChange}
          validate={(val) => (val ? '' : 'Password is required')}
          dataTestId='register-password'
        />
      </FormControl>
      {!e2eStub &&
        <FormControl margin='normal'>
          <Captcha
            action='register'
            onSuccess={handleCaptchaSuccess}
            onError={handleCaptchaError}
          />
        </FormControl>}
    </form>
  );
};

RegisterForm.propTypes = {
  onChange: PropTypes.func,
  onComplete: PropTypes.func,
  onError: PropTypes.func
};

export default RegisterForm;
